import { buildElectionUrl, obtenerURLImagen } from "../../utils/dataFormatter";
import { generateValidUrl } from "../../utils/urlGenerator";
import { getCodeLetter } from "../../utils/dataFormatter";
export class BaseElectionPost {
  constructor(config = {}) {
    this.electionType = config.electionType || 'general';
    this.thresholds = config.thresholds || {
      low: 5,
      medium: 25,
      high: 50
    };
  }

  /**
   * Método principal para generar el post
   */
  async generate({ apiData, params, mappings, menu }) {
    const formattedData = this.formatData(apiData, params);
    const content = this.createContent(formattedData, params);
    const districts = this.generateDistricts(apiData, params);
    const meta = this.generateMeta(content, formattedData, params);
    const codeLetter = getCodeLetter(params.resultPath);
    let typeElection = params?.typeElection;
    const imageData = obtenerURLImagen(codeLetter, typeElection, formattedData.mesasEscrutadas);
    const imageUrl = imageData?.url || "https://www.clarin.com/img/2023/10/06/OHw4KnSeM_1256x620__1.jpg";
    const url = buildElectionUrl(params)

    return {
      _id: `${params.codeLetter}${params.codeNumber || ''}`,
      content,
      url: url,
      data: apiData,
      districts,
      provincias: this.getProvincias(params, mappings),
      distritosProvincia: [],
      gobernadorSitemap: [],
      textSeparador: this.getTextSeparador(formattedData.provincia),
      textSeparadorProvincias: "Mirá los resultados en las demás provincias de Argentina",
      imgPostPri: imageUrl,
      related: this.getRelated(imageUrl),
      tags: this.getTags(params),
      meta
    };
  }

  /**
   * Formatear datos de la API
   */
  formatData(apiData, params) {
    const district = this.getDistrict(apiData, params.codeComplet);
    const isDistrict = !!params.codeNumber;

    // Si existen blocks, ordenarlos por porcentaje de votos
    let sortedBlocks = [];
    if (apiData.blocks && Array.isArray(apiData.blocks)) {
      sortedBlocks = [...apiData.blocks].sort((a, b) => {
        const percentA = parseFloat(a.percent) || 0;
        const percentB = parseFloat(b.percent) || 0;
        return percentB - percentA; // Orden descendente
      });
      console.log("Blocks ordenados por porcentaje:", sortedBlocks.map(b => `${b.name}: ${b.percent}%`));
    }

    return {
      last_update: this.getLastUpdate(apiData),
      mesasEscrutadas: isDistrict ? district?.tables_percent : apiData.tables_percent,
      provincia: apiData.name === "Resultados Nacionales" ? "Argentina" : apiData.name,
      localidad: district?.name || "",
      provinciaLocalidad: this.getProvinciaLocalidad(apiData.name, district?.name, isDistrict),
      candidatos: isDistrict ? district?.parties : apiData.parties,
      stats: isDistrict ? district?.stats : apiData.stats,
      blocks: sortedBlocks.length > 0 ? sortedBlocks : undefined,
      ganador: this.getParty(apiData, district, 0, isDistrict),
      segundo: this.getParty(apiData, district, 1, isDistrict),
      tercero: this.getParty(apiData, district, 2, isDistrict),
      cuarto: this.getParty(apiData, district, 3, isDistrict),
      quinto: this.getParty(apiData, district, 4, isDistrict),
    };
  }

  /**
   * Crear contenido del post según porcentaje escrutado
   */
  createContent(formattedData, params) {
    const percent = formattedData.mesasEscrutadas || 0;

    const variant =
      params?.typeElection === "diputados" || params?.typeElection === "senadores"
        ? this.getVariant(percent)
        : "Final";

    return this[`create${variant}Content`](formattedData, params);
  }

  /**
   * Determinar variante según porcentaje
   */
  getVariant(percent) {
    if (percent <= this.thresholds.low) return 'Initial';
    if (percent <= this.thresholds.medium) return 'Early';
    if (percent <= this.thresholds.high) return 'Partial';
    return 'Final';
  }

  /**
   * Métodos helper
   */
  getDistrict(apiData, code) {
    return apiData?.districts?.find(item => item.code === code);
  }

  getParty(apiData, district, index, isDistrict) {
    return isDistrict ? district?.parties?.[index] : apiData?.parties?.[index];
  }

  getProvinciaLocalidad(provincia, localidad, isDistrict) {
    if (!provincia) return "Argentina";
    return isDistrict && localidad ? `${provincia}, ${localidad}` : provincia;
  }

  getImage(codeLetter) {
    const imageData = obtenerURLImagen(codeLetter);
    return imageData?.url || "https://www.clarin.com/img/2023/10/06/OHw4KnSeM_1256x620__1.jpg";
  }

  /**
   * Generar objeto related con imágenes
   */
  getRelated(imageUrl) {
    return {
      images: [
        {
          epigraphe: "Elecciones 2023. Crédito: Clarín",
          type: "image",
          value: {
            entityId: "136922_1",
            author: "REUTER",
            title: "Elecciones 2023. Crédito: Clarín",
            clippings: [
              {
                url: imageUrl,
                _id: "Apertura_full",
                dimensions: {
                  width: 1256,
                  height: 620
                }
              },
              {
                url: imageUrl,
                _id: "col_listado_1x1",
                dimensions: {
                  width: 340,
                  height: 340
                }
              }
            ]
          },
          _id: "_uwmU9FBY"
        }
      ],
      datafactory: [],
      mams: []
    };
  }

  getTextSeparador(provincia) {
    return `Mirá los resultados en los demás distritos de ${provincia || 'Argentina'}`;
  }

  /**
   * Obtener última actualización
   */
  getLastUpdate(apiData) {
    if (apiData?.last_update) {
      return apiData.last_update;
    }
    return new Date().toISOString();
  }

  /**
   * Generar lista de distritos con URLs
   */
  generateDistricts(apiData, params) {
    if (!apiData?.districts || !Array.isArray(apiData.districts)) {
      return [];
    }

    const nameProvincia = apiData.name === "Resultados Nacionales" ? "Argentina" : apiData.name;

    return apiData.districts.map(district => ({
      ...district,
      url: generateValidUrl(params.baseUrl, nameProvincia, district.name, district.code)
    }));
  }

  /**
   * Obtener lista de provincias
   */
  getProvincias(params, mappings) {
    if (!mappings?.provinces) return [];

    const { typeElection, year, baseUrl } = params;
    
    // Provincias con elecciones a gobernador
    const provinciaGobernadoresIds = ['01', '02', '03', '08'];
    
    let provinciasToUse;
    
    if (typeElection === 'presidente') {
      // Todas las provincias para presidente
      provinciasToUse = Object.values(mappings.provinces);
    } else {
      // Solo provincias con gobernador
      provinciasToUse = provinciaGobernadoresIds
        .map(id => mappings.provinces[id])
        .filter(Boolean);
    }

    return provinciasToUse.map(provincia => ({
      id: `provincia_${provincia.id}`,
      name: provincia.name,
      code: provincia.code,
      url: this.generateProvinciaUrl(baseUrl, provincia, typeElection),
      last_update: new Date().getTime(),
      publication_date: `${year}-10-11T07:00:00.000Z`,
      title: `Elecciones ${year}: ${typeElection} en ${provincia.name}`
    }));
  }

  /**
   * Generar URL de provincia
   */
  generateProvinciaUrl(baseUrl, provincia, typeElection) {
    const provinciaSlug = provincia.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");

    return `${baseUrl}${provinciaSlug}_8_${provincia.code}.html`;
  }

  // Métodos abstractos a implementar en clases hijas
  createInitialContent(data, params) {
    throw new Error("Método createInitialContent debe ser implementado");
  }

  createEarlyContent(data, params) {
    throw new Error("Método createEarlyContent debe ser implementado");
  }

  createPartialContent(data, params) {
    throw new Error("Método createPartialContent debe ser implementado");
  }

  createFinalContent(data, params) {
    throw new Error("Método createFinalContent debe ser implementado");
  }

  getTags(params) {
    throw new Error("Método getTags debe ser implementado");
  }

  generateMeta(content, data, params) {
    throw new Error("Método generateMeta debe ser implementado");
  }
}